import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Phone, Check, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Primitives";
import { CITIES, SERVICES, BUSINESS } from "@/data/site";
import { trackEvent } from "@/lib/api";

export default function CityDetail() {
  const { cityId } = useParams();
  const city = CITIES.find((c) => c.id === cityId);
  if (!city) return <Navigate to="/service-areas" replace />;

  return (
    <>
      <Seo
        title={`Sitters in ${city.name}, TX`}
        description={`Trusted, 11-step screened babysitters and caregivers in ${city.name}, TX from Mom's Best Friend Sitters. Babysitting, hotel care, events and overnight care.`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: `${BUSINESS.name} — ${city.name}`,
          areaServed: `${city.name}, TX`,
          telephone: BUSINESS.phone,
        }}
        breadcrumbs={[{ name: "Home", item: "/" }, { name: "Service Areas", item: "/service-areas" }, { name: city.name, item: `/service-areas/${city.id}` }]}
      />

      <section className="relative">
        <div className="h-[280px] md:h-[360px] overflow-hidden">
          <img src={city.image} alt={`${city.name}, Texas`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-espresso/60" />
        </div>
        <div className="absolute inset-0 flex items-center">
          <div className="container-mbf">
            <Link to="/service-areas" className="inline-flex items-center gap-1.5 text-sm text-parchment font-semibold mb-4 hover:gap-2.5 transition-all" data-testid="city-back-link">
              <ArrowLeft className="w-4 h-4" /> All service areas
            </Link>
            <div className="flex items-center gap-2 text-gold mb-2">
              <MapPin className="w-5 h-5" />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold">Now serving</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white tracking-tight">Sitters in {city.name}, TX</h1>
          </div>
        </div>
      </section>

      <section className="container-mbf py-20 md:py-28 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <p className="overline mb-3">Local, trusted, screened</p>
          <h2 className="font-serif text-2xl md:text-3xl text-espresso mb-5">Childcare that {city.name} families rely on</h2>
          <p className="text-base text-stoneish leading-relaxed mb-4">{city.blurb}</p>
          <p className="text-base text-stoneish leading-relaxed mb-8">
            Every caregiver we send to {city.name} has cleared our full 11-step screening and holds current CPR & First Aid certification. Whether it's a date night, a hotel stay, a wedding, or overnight newborn support, we match your family with a caregiver we trust completely — and a real person confirms every detail with you.
          </p>

          <h3 className="font-serif text-xl text-espresso mb-4">Services available in {city.name}</h3>
          <div className="grid sm:grid-cols-2 gap-3 mb-8">
            {SERVICES.map((s) => (
              <Link key={s.id} to={`/services/${s.id}`} className="flex items-center gap-3 bg-white border border-[#F1E6D0] rounded-xl p-4 hover:border-terracotta/40 transition-colors" data-testid={`city-service-${s.id}`}>
                <span className="w-6 h-6 rounded-full bg-sage/15 text-sage flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5" /></span>
                <span className="text-sm font-medium text-espresso">{s.name}</span>
              </Link>
            ))}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-28 bg-parchment-2 rounded-2xl border border-[#F1E6D0] p-8">
            <h3 className="font-serif text-xl text-espresso mb-2">Request care in {city.name}</h3>
            <p className="text-sm text-stoneish mb-6">Tell us what you need and we'll match a screened caregiver near you.</p>
            <Link to="/book" onClick={() => trackEvent("cta_click", { source: `city_${city.id}` })} className="btn-accent w-full mb-3" data-testid="city-request-cta">Request a Sitter <ArrowRight className="w-4 h-4" /></Link>
            <a href={BUSINESS.phoneHref} className="btn-outline w-full" data-testid="city-phone-link"><Phone className="w-4 h-4" /> {BUSINESS.phone}</a>
          </div>
        </div>
      </section>
    </>
  );
}
